import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from './ui/card';
import { CheckCircle2, Circle, ChevronDown, BookOpen, Sparkles, Trophy } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const FAMILY_META = {
  grammar: {
    label: 'Grammar',
    icon: BookOpen,
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
    softBg: 'bg-emerald-50',
    softBorder: 'border-emerald-200',
    tint: 'text-emerald-700',
    barBg: 'bg-emerald-100'
  },
  vocabulary: {
    label: 'Vocabulary',
    icon: Sparkles,
    dot: 'bg-amber-500',
    bar: 'bg-amber-500',
    softBg: 'bg-amber-50',
    softBorder: 'border-amber-200',
    tint: 'text-amber-700',
    barBg: 'bg-amber-100'
  }
};

const TopicsStudiedCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFamily, setExpandedFamily] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/progress/topics-studied`, { headers: getAuthHeader() });
        setData(res.data);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const families = useMemo(() => {
    if (!data) return [];
    return ['grammar', 'vocabulary'].map((fam) => {
      const summary = fam === 'grammar' ? data.grammar_summary : data.vocabulary_summary;
      // Merge grammar_cloze + grammar_mcq into one unified "Grammar" list
      // (both sections share the exact same taxonomy pool)
      const pool = new Map();
      for (const section of data.sections) {
        if (section.family !== fam) continue;
        for (const t of section.topics) {
          const prev = pool.get(t.topic);
          const combinedCount = (prev?.count || 0) + t.count;
          const studied = (prev?.studied || false) || t.studied;
          pool.set(t.topic, { topic: t.topic, studied, count: combinedCount });
        }
      }
      const topics = Array.from(pool.values());
      return {
        family: fam,
        ...FAMILY_META[fam],
        summary,
        topics
      };
    });
  }, [data]);

  if (loading) {
    return (
      <Card className="p-6 rounded-3xl border-2 border-slate-200">
        <div className="animate-pulse h-4 bg-slate-200 rounded w-40 mb-3" />
        <div className="animate-pulse h-3 bg-slate-100 rounded w-full" />
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card className="p-6 rounded-3xl border-2 border-slate-200 shadow-sm" data-testid="topics-studied-card">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full mb-2">
            <Trophy size={10} /> Collect them all
          </div>
          <h3 className="text-xl font-heading font-black text-slate-900">Topics I've studied</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Every question you practise unlocks a rule from the {data.level} taxonomy. Tap a family to see what's next.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {families.map((fam) => {
          const Icon = fam.icon;
          const isOpen = expandedFamily === fam.family;
          const studied = fam.topics.filter((t) => t.studied);
          const remaining = fam.topics.filter((t) => !t.studied);
          const total = fam.topics.length;
          const studiedCount = studied.length;
          const pct = total ? Math.round((studiedCount / total) * 100) : 0;
          return (
            <div
              key={fam.family}
              className={`rounded-2xl border-2 ${fam.softBorder} ${fam.softBg} overflow-hidden`}
              data-testid={`topics-family-${fam.family}`}
            >
              <button
                type="button"
                onClick={() => setExpandedFamily(isOpen ? null : fam.family)}
                className="w-full text-left px-4 py-3 flex items-center gap-3"
                data-testid={`topics-family-toggle-${fam.family}`}
              >
                <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center bg-white shadow-sm ${fam.tint}`}>
                  <Icon size={18} strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className={`font-heading font-black text-slate-900`}>{fam.label}</div>
                    <div className={`text-xs font-bold ${fam.tint}`}>
                      {studiedCount}/{total} · {pct}%
                    </div>
                  </div>
                  <div className={`w-full h-2 rounded-full ${fam.barBg} overflow-hidden`}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                      className={`h-full ${fam.bar}`}
                    />
                  </div>
                </div>
                <ChevronDown
                  size={18}
                  className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 pt-1 border-t border-white/60">
                      {studied.length > 0 && (
                        <div className="mb-3">
                          <div className={`text-[11px] font-black uppercase tracking-wide ${fam.tint} mb-2`}>
                            ✅ Studied ({studied.length})
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1.5">
                            {studied.map((t) => (
                              <div key={t.topic} className="flex items-start gap-2 text-xs text-slate-700">
                                <CheckCircle2 size={14} className={`flex-shrink-0 mt-0.5 ${fam.tint}`} />
                                <span className="leading-snug">
                                  {t.topic}
                                  {t.count > 1 && (
                                    <span className={`ml-1 text-[10px] font-bold ${fam.tint}`}>×{t.count}</span>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {remaining.length > 0 && (
                        <div>
                          <div className="text-[11px] font-black uppercase tracking-wide text-slate-400 mb-2">
                            ⬜ Not yet studied ({remaining.length})
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1.5">
                            {remaining.map((t) => (
                              <div key={t.topic} className="flex items-start gap-2 text-xs text-slate-500">
                                <Circle size={14} className="flex-shrink-0 mt-0.5 text-slate-300" />
                                <span className="leading-snug">{t.topic}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {studied.length === 0 && (
                        <div className="text-xs text-slate-500 py-2">
                          You haven't practised any {fam.label.toLowerCase()} rules yet — head to any Grammar
                          or Vocabulary module to start unlocking topics.
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default TopicsStudiedCard;
